import { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, X, Check, FlipHorizontal, Zap, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

/**
 * WebcamCapture Component - 2026 UX/UI Design
 * Features:
 * - Full-screen camera mode
 * - Face detection overlay (animated dashed border)
 * - 3-2-1 countdown before capture
 * - Mirror toggle button
 * - Flash effect on capture
 * - Review screen with retake/confirm
 */
const WebcamCapture = ({ onCapture, open, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [captured, setCaptured] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [isMirrored, setIsMirrored] = useState(true);
  const [countdown, setCountdown] = useState(null);
  const [showFlash, setShowFlash] = useState(false);
  const [facingMode, setFacingMode] = useState('user');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [open, facingMode]);

  const startCamera = async () => {
    setIsLoading(true);
    try {
      // Stop existing stream first
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Unable to access camera. Please check permissions.');
    } finally {
      setIsLoading(false);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCaptured(false);
    setCapturedImage(null);
    setCountdown(null);
    setShowFlash(false);
  };

  const isMirroredRef = useRef(isMirrored);
  isMirroredRef.current = isMirrored;

  const performCapture = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Mirror the canvas if mirrored view is enabled
      if (isMirroredRef.current) {
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
      }

      context.drawImage(video, 0, 0);

      // Reset transform
      context.setTransform(1, 0, 0, 1, 0, 0);

      // Show flash effect
      setShowFlash(true);
      setTimeout(() => setShowFlash(false), 300);

      canvas.toBlob((blob) => {
        const imageUrl = URL.createObjectURL(blob);
        setCapturedImage(imageUrl);
        setCaptured(true);
      }, 'image/jpeg', 0.9);
    }
  }, []);

  const startCountdown = useCallback(() => {
    // Show 3
    setCountdown(3);

    // After 1s, show 2
    setTimeout(() => {
      setCountdown(2);
    }, 1000);

    // After 2s, show 1
    setTimeout(() => {
      setCountdown(1);
    }, 2000);

    // After 3s, capture
    setTimeout(() => {
      setCountdown(null);
      performCapture();
    }, 3000);
  }, [performCapture]);

  const handleCapture = () => {
    startCountdown();
  };

  const handleInstantCapture = () => {
    performCapture();
  };

  const handleRetake = () => {
    setCaptured(false);
    setCapturedImage(null);
    setCountdown(null);
    // Restart camera if it's not already running
    if (!stream || !videoRef.current?.srcObject) {
      startCamera();
    }
  };

  const handleUse = () => {
    if (canvasRef.current) {
      canvasRef.current.toBlob((blob) => {
        const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
        onCapture(file);
        handleClose();
      }, 'image/jpeg', 0.9);
    }
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  const toggleMirror = () => {
    setIsMirrored(prev => !prev);
  };

  const toggleCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden bg-black/95 border-0">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-20 p-4 bg-gradient-to-b from-black/80 to-transparent">
          <div className="flex items-center justify-between">
            <div className="text-white">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Camera className="w-5 h-5" />
                Take a Selfie
              </h2>
              <p className="text-xs text-white/70">Position your face in the frame</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="text-white hover:bg-white/20 rounded-full h-10 w-10"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Camera View */}
        <div className="relative bg-black aspect-[4/3] sm:aspect-video">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-30">
              <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Flash Effect */}
          {showFlash && (
            <div className="absolute inset-0 bg-white z-40 animate-flash" />
          )}

          {/* Countdown Overlay */}
          {countdown !== null && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-50">
              <div
                className="text-[150px] font-black text-white drop-shadow-2xl"
                style={{
                  textShadow: '0 0 40px rgba(255,255,255,0.5), 0 0 80px rgba(191,33,47,0.8)',
                  animation: 'pulse 0.5s ease-in-out'
                }}
              >
                {countdown}
              </div>
            </div>
          )}

          {!captured ? (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className={`w-full h-full object-cover ${isMirrored ? 'scale-x-[-1]' : ''}`}
              />

              {/* Face Detection Overlay */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div
                  className="
                    w-48 h-64 sm:w-56 sm:h-72
                    border-4 border-dashed border-white/50
                    rounded-[100px]
                    animate-face-pulse
                  "
                />
              </div>

              {/* Corner guides */}
              <div className="absolute top-1/4 left-1/4 w-8 h-8 border-t-4 border-l-4 border-white/70 rounded-tl-lg" />
              <div className="absolute top-1/4 right-1/4 w-8 h-8 border-t-4 border-r-4 border-white/70 rounded-tr-lg" />
              <div className="absolute bottom-1/4 left-1/4 w-8 h-8 border-b-4 border-l-4 border-white/70 rounded-bl-lg" />
              <div className="absolute bottom-1/4 right-1/4 w-8 h-8 border-b-4 border-r-4 border-white/70 rounded-br-lg" />
            </>
          ) : (
            <img
              src={capturedImage}
              alt="Captured selfie"
              className="w-full h-full object-cover"
            />
          )}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>

        {/* Controls */}
        <div className="p-4 bg-gradient-to-t from-black/80 to-transparent">
          <div className="flex items-center justify-center gap-4">
            {!captured ? (
              <>
                {/* Mirror Toggle */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleMirror}
                  className={`
                    h-12 w-12 rounded-full border-2
                    ${isMirrored
                      ? 'bg-white/20 border-white text-white'
                      : 'bg-transparent border-white/50 text-white/50'
                    }
                    hover:bg-white/30
                    transition-all duration-200
                  `}
                  title={isMirrored ? 'Mirror: On' : 'Mirror: Off'}
                >
                  <FlipHorizontal className="w-5 h-5" />
                </Button>

                {/* Main Capture Button */}
                <button
                  onClick={handleCapture}
                  disabled={countdown !== null}
                  className="
                    relative w-20 h-20 rounded-full
                    bg-white
                    flex items-center justify-center
                    hover:scale-105 active:scale-95
                    transition-transform duration-200
                    disabled:opacity-50 disabled:cursor-not-allowed
                    focus:outline-none focus:ring-4 focus:ring-white/50
                  "
                  data-testid="capture-selfie-button"
                >
                  {/* Inner ring */}
                  <div className="absolute inset-2 rounded-full border-4 border-black/20" />
                  {/* Center dot */}
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#BF212F] to-[#F26B38]" />
                </button>

                {/* Camera Switch (for mobile) */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleCamera}
                  className="
                    h-12 w-12 rounded-full border-2 border-white/50
                    bg-transparent text-white/50
                    hover:bg-white/20 hover:border-white hover:text-white
                    transition-all duration-200
                  "
                  title="Switch Camera"
                >
                  <RefreshCw className="w-5 h-5" />
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={handleRetake}
                  className="
                    h-14 px-6
                    bg-white/10 border-white/30 text-white
                    hover:bg-white/20 hover:border-white
                    rounded-2xl
                    flex items-center gap-2
                    transition-all duration-200
                  "
                >
                  <Camera className="w-5 h-5" />
                  <span>Retake</span>
                </Button>
                <Button
                  onClick={handleUse}
                  className="
                    h-14 px-8
                    bg-gradient-to-r from-[#BF212F] to-[#F26B38]
                    hover:from-[#8B1923] hover:to-[#BF212F]
                    text-white border-0
                    rounded-2xl
                    flex items-center gap-2
                    shadow-lg shadow-[#BF212F]/30
                    transition-all duration-200 hover:scale-105
                  "
                  data-testid="use-selfie-button"
                >
                  <Check className="w-5 h-5" />
                  <span>Use This Photo</span>
                </Button>
              </>
            )}
          </div>

          {/* Quick capture hint */}
          {!captured && (
            <p className="text-center text-white/50 text-xs mt-3">
              Tap the button for a 3-second countdown, or{' '}
              <button
                onClick={handleInstantCapture}
                className="text-white/70 underline hover:text-white"
              >
                capture instantly
              </button>
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WebcamCapture;
